import {
  createStyles,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import {
  TwilioStatus,
  TwilioAPIStatus,
  useAppStateContext,
  LivekitStatus,
} from '../../../AppStateProvider/AppStateProvider';
import { TWILIO } from '../../../../constants';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '100%',
      maxWidth: '600px',
    },
    content: {
      padding: '3em',
      [theme.breakpoints.down('sm')]: {
        padding: '2em',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: '0.3em',
      },
    },
  })
);

const twilioServiceStatusObj = {
  operational: { status: 'Up', icon: <SuccessStatus /> },
  major_outage: { status: 'Major Outage', icon: <ErrorStatus /> },
  partial_outage: { status: 'Partial Outage', icon: <WarningStatus /> },
  degraded_performance: { status: 'Degraded', icon: <WarningStatus /> },
};

const livekitServiceStatusObj: any = {
  idle: { status: 'Degraded', icon: <WarningStatus /> },
  running: { status: 'Partial Outage', icon: <WarningStatus /> },
  skipped: { status: 'Partial Outage', icon: <WarningStatus /> },
  success: { status: 'Up', icon: <SuccessStatus /> },
  failed: { status: 'Major Outage', icon: <ErrorStatus /> },
};

export function TwilioStatusRow({ status, serviceName }: { status?: TwilioAPIStatus; serviceName: string }) {
  const classes = useStyles();
  const { state } = useAppStateContext();
  const isTwilio = state.selectedProvider === TWILIO;
  const serviceStatusObj = isTwilio ? twilioServiceStatusObj : livekitServiceStatusObj;
  const serviceStatus = status ? serviceStatusObj[status] : null;

  return (
    <TableRow>
      <TableCell>
        <div className={classes.iconContainer}>
          {serviceStatus?.icon}
          <Typography variant="body1">
            <strong>{serviceStatus?.status}</strong>
          </Typography>
        </div>
      </TableCell>
      <TableCell>
        <Typography variant="body1">{serviceName}</Typography>
      </TableCell>
    </TableRow>
  );
}

interface ConnectionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  serviceStatuses: TwilioStatus | LivekitStatus | null;
  signalingGateway?: string;
  turnServers?: string;
}

const twilioConnectionTests: (keyof TwilioStatus)[] = [
  'Group Rooms',
  'Go Rooms',
  'Peer-to-Peer Rooms',
  'Recordings',
  'Compositions',
  'Network Traversal Service',
];

const livekitConnectionTests: (keyof LivekitStatus)[] = [
  'Signal Connection',
  'Webrtc Connection',
  'Turn',
  'Publish Audio',
  'Publish Video',
  'Reconnection',
];

export function ConnectionModal({
  isModalOpen,
  setIsModalOpen,
  serviceStatuses,
  signalingGateway,
  turnServers,
}: ConnectionModalProps) {
  const classes = useStyles();
  const { state } = useAppStateContext();
  const isTwilio = state.selectedProvider === TWILIO;
  const connectionTests = isTwilio ? twilioConnectionTests : livekitConnectionTests;

  return (
    <>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3">Detailed connection information</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Status</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Type</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connectionTests.map((test, index) => (
                <TwilioStatusRow key={index} status={(serviceStatuses as any)?.[test]} serviceName={test} />
              ))}
              {isTwilio && (
                <>
                  <TableRow>
                    <TableCell>
                      <div className={classes.iconContainer}>
                        {signalingGateway === 'Reachable' ? <SuccessStatus /> : <ErrorStatus />}
                        <Typography variant="body1">
                          <strong>{signalingGateway}</strong>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">Signaling Gateway</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <div className={classes.iconContainer}>
                        {turnServers === 'Reachable' ? <SuccessStatus /> : <ErrorStatus />}
                        <Typography variant="body1">
                          <strong>{turnServers}</strong>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">TURN Servers</Typography>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
